/**
 * Product Tabs - Tab switching and accessibility
 */
// Toggle to enable/disable console debug logging for optional-tab detection.
const PRODUCT_TABS_DEBUG = true;
const ptLog = (...args) => {
  if (PRODUCT_TABS_DEBUG) console.log('[ProductTabs]', ...args);
};

class ProductTabs extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll('[role="tab"]');
    this.panels = this.querySelectorAll('[role="tabpanel"]');
    if (!this.tabs.length) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick.bind(this));
      tab.addEventListener('keydown', this.onKeyDown.bind(this));
    });

    this.initOptionalTabs();
    this.initReviewsLoginGate();
    this.initVariantSync();
  }

  /**
   * Swap the Description and More Information tab content when the selected
   * variant changes, mirroring the Magento configurable child-description
   * behaviour. Content comes from variant metafields custom.description and
   * custom.more_information (emitted as a JSON map in the section); empty
   * values fall back to the product-level HTML rendered on load.
   */
  initVariantSync() {
    const dataEl = this.querySelector('[data-variant-tab-data]');
    if (!dataEl) return;

    try {
      this.variantTabData = JSON.parse(dataEl.textContent);
    } catch (e) {
      ptLog('initVariantSync: failed to parse variant tab data', e);
      return;
    }

    this.descBody = this.querySelector('[data-variant-description]');
    this.moreInfoBody = this.querySelector('[data-variant-more-info]');
    this.variantRows = this.querySelectorAll('[data-variant-row]');
    const fallback = this.variantTabData.fallback || {};
    this.descFallback = fallback.description != null ? fallback.description : '';
    this.moreInfoFallback = fallback.more_information != null ? fallback.more_information : '';

    this.hideEmptyRowsOnLoad();

    if (typeof subscribe === 'function') {
      subscribe('variant-change', (payload) => {
        const variant = payload && payload.data && payload.data.variant;
        if (variant) this.applyVariantContent(variant.id);
      });
    }
  }

  /**
   * Hide rows that rendered empty server-side. Liquid hides rows whose
   * metafield value is blank, but a value that renders as empty markup still
   * reaches the page as a visible label with no value.
   */
  hideEmptyRowsOnLoad() {
    if (!this.variantRows || !this.variantRows.length) return;

    let hidden = 0;
    this.variantRows.forEach((valueEl) => {
      const item = valueEl.closest('[data-variant-row-item]');
      if (!item || item.hidden) return;
      if (this.rowHasValue(valueEl.innerHTML)) return;
      item.hidden = true;
      hidden += 1;
    });

    ptLog('hideEmptyRowsOnLoad: hid', hidden, 'empty row(s)');
  }

  applyVariantContent(variantId) {
    if (!this.variantTabData || !variantId) return;
    const data = this.variantTabData.variants && this.variantTabData.variants[variantId];
    if (!data) return;

    if (this.descBody) {
      const html = (data.description != null && String(data.description).trim() !== '')
        ? data.description
        : this.descFallback;
      if (html != null) this.descBody.innerHTML = html;
    }

    if (this.moreInfoBody) {
      const html = (data.more_information != null && String(data.more_information).trim() !== '')
        ? data.more_information
        : this.moreInfoFallback;
      if (html != null) this.moreInfoBody.innerHTML = html;
    }

    this.applyVariantRows(data.rows);

    ptLog('applyVariantContent: updated tabs for variant', variantId);
  }

  /**
   * A row counts as empty when it has no rendered text and no media/link —
   * metafield_tag can emit markup such as <p></p> for a blank value, which is
   * a non-empty string but displays as an empty row.
   */
  rowHasValue(value) {
    if (value == null || String(value).trim() === '') return false;
    const probe = document.createElement('div');
    probe.innerHTML = value;
    if (probe.textContent.trim().length > 0) return true;
    return probe.querySelector('img, iframe, svg, video, embed, object, a[href]') !== null;
  }

  applyVariantRows(rows) {
    if (!this.variantRows || !this.variantRows.length || !rows) return;

    this.variantRows.forEach((valueEl) => {
      const key = valueEl.getAttribute('data-variant-row');
      if (!Object.prototype.hasOwnProperty.call(rows, key)) return;

      const value = rows[key];
      const hasValue = this.rowHasValue(value);
      valueEl.innerHTML = hasValue ? value : '';

      const item = valueEl.closest('[data-variant-row-item]') || valueEl.closest('.brand-info-item');
      if (item) item.hidden = !hasValue;
    });

    ptLog('applyVariantRows: updated', Object.keys(rows).length, 'row(s)');
  }

  initReviewsLoginGate() {
    const panel = this.querySelector('#product-reviews');
    if (!panel || panel.dataset.customerLoggedIn === 'true') return;

    const loginUrl = panel.dataset.loginUrl || '/account/login';
    const loginLabel = panel.dataset.loginLabel || 'Login to write a review';

    const findWriteButton = () =>
      Array.from(panel.querySelectorAll('button.kl_reviews__button')).find((el) => {
        const label = (el.getAttribute('aria-label') || '') + ' ' + (el.textContent || '');
        return /write a review/i.test(label);
      });

    const addLoginLink = () => {
      if (panel.querySelector('.product-tabs__reviews-login')) return;
      const btn = findWriteButton();
      if (!btn) return;
      const link = document.createElement('a');
      link.className = 'product-tabs__reviews-login kl_reviews__button btn button';
      link.href = loginUrl;
      link.textContent = loginLabel;
      btn.parentNode.insertBefore(link, btn);
    };

    addLoginLink();
    new MutationObserver(addLoginLink).observe(panel, { childList: true, subtree: true });
  }

  initOptionalTabs() {
    const optionalItems = this.querySelectorAll('[data-optional-tab]');
    ptLog('initOptionalTabs: found', optionalItems.length, 'optional tab(s)');
    if (!optionalItems.length) return;

    this.tabsList = this.querySelector('[role="tablist"]');
    this.updateTabCount();

    optionalItems.forEach((item) => {
      const name = item.getAttribute('data-optional-tab');
      const tab = item.querySelector('[role="tab"]');
      const panelId = tab && tab.getAttribute('aria-controls');
      const panel = panelId ? this.querySelector(`#${panelId}`) : null;
      if (!panel) {
        ptLog(`[${name}] no panel found (panelId=${panelId}) — skipping`);
        return;
      }

      const content = panel.querySelector('.product-tabs__content') || panel;

      const reveal = (reason) => {
        ptLog(`[${name}] revealing tab (${reason})`);
        item.hidden = false;
        item.classList.remove('product-tabs__item--optional');
        this.updateTabCount();
      };

      if (this.hasContent(content, name)) {
        reveal('content present on load');
        return;
      }

      ptLog(`[${name}] empty on load — watching for async content`);

      const observer = new MutationObserver(() => {
        ptLog(`[${name}] mutation observed — re-checking content`);
        if (this.hasContent(content, name)) {
          reveal('content injected asynchronously');
          observer.disconnect();
        }
      });
      observer.observe(content, { childList: true, subtree: true, characterData: true });

      window.setTimeout(() => {
        observer.disconnect();
        ptLog(`[${name}] observer timed out — tab stays ${item.hidden ? 'hidden' : 'visible'}`);
      }, 10000);
    });
  }

  /** Keep --product-tabs-count in sync with the number of visible tab items. */
  updateTabCount() {
    if (!this.tabsList) return;
    const visible = Array.from(
      this.querySelectorAll('.product-tabs__item')
    ).filter((item) => !item.hidden);
    this.tabsList.style.setProperty('--product-tabs-count', visible.length);
    ptLog('updateTabCount: visible tabs =', visible.length);
  }

  hasContent(el, name) {
    if (!el) {
      ptLog(`[${name}] hasContent: no element → false`);
      return false;
    }
    const text = el.textContent.trim();
    if (text.length > 0) {
      ptLog(`[${name}] hasContent: text found (${text.length} chars) → true`, JSON.stringify(text.slice(0, 80)));
      return true;
    }
    const media = el.querySelector('img, iframe, svg, video, embed, object, a[href]');
    if (media) {
      ptLog(`[${name}] hasContent: no text but found media/link → true`, media.tagName);
      return true;
    }
    ptLog(`[${name}] hasContent: no text, no media → false`);
    return false;
  }

  onTabClick(e) {
    const tab = e.currentTarget;
    if (!tab || tab.getAttribute('aria-selected') === 'true') return;
    this.activateTab(tab);
  }

  onKeyDown(e) {
    const tab = e.currentTarget;
    let newTab = null;

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        newTab = this.getPrevTab(tab);
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        newTab = this.getNextTab(tab);
        break;
      case 'Home': {
        e.preventDefault();
        const visible = this.visibleTabs();
        newTab = visible[0];
        break;
      }
      case 'End': {
        e.preventDefault();
        const visible = this.visibleTabs();
        newTab = visible[visible.length - 1];
        break;
      }
      default:
        return;
    }

    if (newTab) {
      this.activateTab(newTab);
      newTab.focus();
    }
  }

  /** Tabs that are currently visible (excludes hidden optional tabs). */
  visibleTabs() {
    return Array.from(this.tabs).filter((tab) => {
      const item = tab.closest('.product-tabs__item');
      return !item || !item.hidden;
    });
  }

  getPrevTab(tab) {
    const visible = this.visibleTabs();
    const index = visible.indexOf(tab);
    return index > 0 ? visible[index - 1] : visible[visible.length - 1];
  }

  getNextTab(tab) {
    const visible = this.visibleTabs();
    const index = visible.indexOf(tab);
    return index < visible.length - 1 ? visible[index + 1] : visible[0];
  }

  activateTab(selectedTab) {
    const panelId = selectedTab.getAttribute('aria-controls');
    const panel = panelId
      ? this.querySelector(`#${panelId}`) ||
        this.querySelector(`[data-panel-id="${panelId}"]`)
      : null;

    this.tabs.forEach((t) => {
      t.setAttribute('aria-selected', t === selectedTab ? 'true' : 'false');
      t.classList.toggle('product-tabs__tab--active', t === selectedTab);
    });

    this.panels.forEach((p) => {
      const isActive = p === panel;
      p.classList.toggle('product-tabs__panel--active', isActive);
    });
  }
}

customElements.define('product-tabs', ProductTabs);
